import type { SelectorVersion, StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import type { ItemSelector, Selector, ValueKind } from "../../../api/content/authoring/selectorDecoders";
import { JsonContractError } from "../../../api/http/jsonDecoding";

/**
 * Which selectors a source may use, and the first value of each kind (AU-4). The kinds and
 * versions come from the server's step catalogue; a tool input offers only selectors whose
 * value fits what it accepts (P-6), a placeholder every selector that renders text.
 */

/** `kind@version`, the value of a selector choice. */
export function selectorChoiceKey(selector: Pick<SelectorVersion, "kind" | "version">): string {
  return `${selector.kind}@${String(selector.version)}`;
}

export function selectorChoices(catalogue: StepCatalogue | null, accepts: readonly ValueKind[] | null, current: Selector | null): readonly SelectorVersion[] {
  const offered = (catalogue?.selectors ?? []).filter((selector) => accepts === null ? selector.rendersText : selector.valueKind !== null && accepts.includes(selector.valueKind));
  // A selector the draft already uses stays choosable, so a stored step can be shown as it is.
  if (current !== null && !offered.some((selector) => selector.kind === current.kind && selector.version === current.version)) {
    return [...offered, { kind: current.kind, version: current.version, valueKind: null, rendersText: false }];
  }
  return offered;
}

/** A new selector of a kind and version; item selectors start floating on `firstItem`. */
export function newSelector(choice: Pick<SelectorVersion, "kind" | "version">, firstItem: string | null, firstLabelList: string | null): Selector {
  const { kind, version } = choice;
  switch (kind) {
    case "source_version": return { kind, version: 1, paths: [] };
    case "earlier_results": return { kind, version: 1 };
    case "accepted_instance_data":
      if (version !== 1 && version !== 2) throw new JsonContractError("Unknown accepted_instance_data version.");
      return { kind, version };
    case "reference_vocabularies": return { kind, version: 1 };
    case "derived_records": return { kind, version: 1, record_kind: "corpus-vectors/1", documents_of: "candidates" };
    case "source_scope": return { kind, version: 1, scope: { corpus: "bound" } };
    case "item":
      return { kind, version, item: { item: firstItem ?? "" } };
    case "subject_text":
      return { kind, version, scope: "document", render: "plain" };
    case "label_list":
      return { kind, version, label_list: { item: firstLabelList ?? "" }, include_guidelines: true, include_examples: "none" };
    case "competency_questions":
      return { kind, version, tags: [], include_sparql: false };
    case "ontology_terms":
      return { kind, version, term_kinds: ["class"], modules: [], filter: "all", render: version >= 2 ? "term_cards" : "turtle", annotation_properties: [] };
    case "few_shot_examples":
      return { kind, version, examples: { source: "tags", tags: ["example"] }, maximum_examples: 3, include_notes: true };
  }
}

/** The item an item selector names: an item ID (floating) or a `VersionRef` (pinned); none
 * for a role, which the project's bindings fill (process-governance.md PG-5). */
export function selectedItemId(selector: ItemSelector): string {
  if ("item" in selector) {
    return selector.item;
  }
  return "version" in selector ? (selector.version.split("@")[0] ?? selector.version) : "";
}

/** Floating reads the item as of the Thread's content version; pinned reads one version. */
export function itemSelectorOf(itemId: string, pinnedReference: string | null): ItemSelector {
  return pinnedReference === null ? { item: itemId } : { version: pinnedReference };
}
